import { type ReactNode, useLayoutEffect, useRef } from 'react'

interface MiddleEllipsisProps {
  children?: ReactNode
  width?: number | string
}

/**
 * Truncates its first child's text in the middle so it fits the width.
 */
export function MiddleEllipsis({ children, width }: MiddleEllipsisProps) {
  const ref = useRef<HTMLDivElement>(null)

  // Re-measures whenever the width or text changes.
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) {
      return
    }

    const onResize = () => prepEllipse(node)
    window.addEventListener('resize', onResize)
    prepEllipse(node)
    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [width, children])

  return (
    <div
      ref={ref}
      style={{
        wordBreak: 'keep-all',
        overflowWrap: 'normal',
        ...(width && { width }),
      }}
    >
      {children}
    </div>
  )
}

const ellipse = (
  parentNode: HTMLElement,
  childNode: HTMLElement,
  txtNode: HTMLElement,
) => {
  const childWidth = childNode.offsetWidth
  const containerWidth = parentNode.offsetWidth
  const txtWidth = txtNode.offsetWidth
  const targetWidth = childWidth > txtWidth ? childWidth : txtWidth

  if (targetWidth > containerWidth) {
    if (!txtNode.textContent) {
      return
    }
    const str = txtNode.textContent
    const txtChars = str.length
    const avgLetterSize = txtWidth / txtChars
    const canFit = (containerWidth - (targetWidth - txtWidth)) / avgLetterSize
    const delEachSide = (txtChars - canFit + 5) / 2
    const endLeft = Math.floor(txtChars / 2 - delEachSide)
    const startRight = Math.ceil(txtChars / 2 + delEachSide)

    txtNode.setAttribute('data-original', txtNode.textContent)
    txtNode.textContent =
      str.substring(0, endLeft) + '...' + str.substring(startRight)
  }
}

function prepEllipse(node: HTMLElement) {
  const parent = node.parentElement
  const child = node.childNodes[0] as HTMLElement
  const txtToEllipse = child

  if (parent && child && txtToEllipse) {
    // (Re)-set text back to data-original-text if it exists.
    if (txtToEllipse.hasAttribute('data-original')) {
      txtToEllipse.textContent = txtToEllipse.getAttribute('data-original')
    }

    ellipse(
      // Use the smaller width.
      node.offsetWidth > parent.offsetWidth ? parent : node,
      child,
      txtToEllipse,
    )
  }
}
